import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InquiryService } from '../../../services/inquiry.service';
import { Inquiry, InquiryPriority, InquiryStatus, InquiryStatusMap } from '../../../models/inquiry.model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { HttpResponse } from '@angular/common/http';
import { ModalSnackbarService } from '../../../services/modal-snackbar.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { AdminUser, AdminUsersService } from '../../../services/admin-users.service';
import { UserInfoComponent } from '../user-info/user-info.component';
import { MatAccordion } from '@angular/material/expansion';
import { blankOpen, formatAmount, formatDate, truncateDecimals } from '../../../misc/functions';
import { Currencies } from '../../../models/monthly-report.model';
import { DecimalInputComponent } from '../../common/decimal-input.component';
import { map, startWith } from 'rxjs/operators';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DecimalInputDirective } from '../../common/decimal-input.directive';

@Component({
  selector: 'app-admin-inquiry-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MaterialModule,
    UserInfoComponent,
    DecimalInputComponent,
    MatAutocompleteModule,
    DecimalInputDirective
  ],
  templateUrl: './admin-inquiry-detail.component.html',
  styleUrls: ['./admin-inquiry-detail.component.scss']
})
export class AdminInquiryDetailComponent implements OnInit {

  formatDate = formatDate;
  formatAmount = formatAmount;
  Currencies = Currencies;

  adminForm!: FormGroup;
  priorityOptions = InquiryPriority;

  inquiry!: Inquiry;
  form!: FormGroup;
  selectedFiles: File[] = [];

  adminUsers: AdminUser[] = [];
  isLoaded = false;

  subCategories: string[] = [];
  filteredSubCategories: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private inquiryService: InquiryService,
    private fb: FormBuilder,
    private snackBar: ModalSnackbarService,
    private fileUtils: FileUtilsService,
    public adminUsersService: AdminUsersService,
    private router: Router
  ) {
    const st = history.state as { subCategories?: string[] };
    this.subCategories = st.subCategories?.slice().sort((a, b) => a.localeCompare(b, 'ja')) ?? [];
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));

    this.adminUsersService.getAdminUsers().subscribe(res => {
      this.adminUsers = this.adminUsersService.parseData(res);
      this.isLoaded = true;
      this.loadInquiry(id);
    });

    this.adminForm = this.fb.group({
      sub_category: [''],
      status: ['', Validators.required],
      priority: ['', Validators.required],
      remark: ['', Validators.maxLength(4096)],
      admin_user_id: [null],
      requested_amount: [''],
      currency: [''],
    });

    this.form = this.fb.group({
      body: ['', [Validators.required, Validators.maxLength(1000)]],
      files: [[], this.fileUtils.fileSizeValidator(20 * 1024 * 1024)] // 20MB
    });

    this.filteredSubCategories = [...this.subCategories];

    const control = this.adminForm.get('sub_category');
    control?.valueChanges.pipe(
      startWith(control.value ?? ''),
      map(v => String(v ?? '').trim()),
      map(v => this.filterSubCategories(v))
    ).subscribe(list => this.filteredSubCategories = list);
  }

  onSubCategoryClick(trigger: MatAutocompleteTrigger) {
    // ① フォーカス時は常に全件表示に戻す
    this.filteredSubCategories = [...this.subCategories];

    // ② パネルを開く（レンダリングのタイミング対策で microtask）
    Promise.resolve().then(() => trigger.openPanel());
  }

  private filterSubCategories(value: string): string[] {
    if (!value) return [...this.subCategories];
    const v = value.toLowerCase();
    return this.subCategories.filter(x => x.toLowerCase().includes(v));
  }

  get statusOptions() {
    // 出金依頼が「出金額確定」になっていたら変更可能なのは「出金済」のみ（しかも未出金が０）
    if (this.inquiry.status === '出金額確定') {
      if (this.inquiry?.withdrawal_transaction?.unpaid_amount == 0) {
        return ['出金額確定', '出金済'];
      }
      else {
        return ['出金額確定'];
      }
    }

    return InquiryStatusMap[this.inquiry.category].filter(s => s !== '完了' && s !== '取消');
  }

  loadInquiry(id: number): void {
    this.inquiryService.get(id, true).subscribe({
      next: (res) => {
        this.inquiry = res;
        const rawAmount = this.inquiry.withdrawal_transaction?.requested_amount;

        this.adminForm.patchValue({
          sub_category: this.inquiry.sub_category,
          status: this.inquiry.status,
          priority: this.inquiry.priority,
          remark: this.inquiry.remark,
          admin_user_id: this.inquiry.admin_user_id ?? '__unassigned__',
          requested_amount: (rawAmount != null && !Number.isNaN(Number(rawAmount))) ? truncateDecimals(Number(rawAmount), this.inquiry.withdrawal_transaction?.currency) : null,
          currency: this.inquiry.withdrawal_transaction?.currency,
        });

      },
      error: () => this.snackBar.show('読み込み失敗')
    });
  }

  onUpdate(): void {
    if (this.adminForm.invalid) return;

    const formValue = this.adminForm.value;
    const payload = {
      ...formValue,
      admin_user_id: formValue.admin_user_id === '__unassigned__' ? null : formValue.admin_user_id
    };

    this.inquiryService.update(this.inquiry.id, payload).subscribe(() => {
      if (this.adminForm.get('status')?.getRawValue() === '回答済') {
        this.snackBar.show('管理者用メモ・担当者・状態・優先度を更新しました', '/admin/inquiry');
      }
      else {
        this.snackBar.show('管理者用メモ・担当者・状態・優先度を更新しました');
        // 更新完了後の処理
        this.loadInquiry(this.inquiry.id);
      }
    });
  }

  submit(): void {
    if (!this.inquiry.admin_user_id) {
      this.snackBar.show('担当者が未割当のまま返信を登録する事はできません。\n先に担当者の割り当てを変更して「更新」を押して下さい。', undefined, 6000);
      return;
    }

    if (this.form.invalid) return;

    const formData = new FormData();
    formData.append('body', this.form.value.body);

    if (this.form.value.files) {
      this.form.value.files.forEach((file: File, i: number) => {
        formData.append('attachments[]', file, file.name);
      });
    }

    this.inquiryService.addChild(this.inquiry.id, formData).subscribe({
      next: () => {
        this.loadInquiry(this.inquiry.id);
        this.form.reset();
        this.selectedFiles = [];
        this.snackBar.show('返信を登録しました');
      },
      error: () => {
        this.snackBar.show('送信に失敗しました');
      }
    });
  }

  onDragOver(event: DragEvent): void {
    this.fileUtils.onDragOver(event);
  }

  onDrop(event: DragEvent): void {
    const newFiles = this.fileUtils.extractValidDroppedFiles(event, this.selectedFiles);
    if (!newFiles) {
      this.snackBar.show(`ファイルは最大 ${this.fileUtils.MAX_FILE_COUNT} 個までです`);
      return;
    }

    this.fileUtils.addFiles(this.selectedFiles, newFiles, files => {
      this.updateSelectedFiles(files);
    });
  }

  onFileChange(event: Event): void {
    const newFiles = this.fileUtils.extractValidSelectedFiles(event, this.selectedFiles);
    if (!newFiles) {
      this.snackBar.show(`ファイルは最大 ${this.fileUtils.MAX_FILE_COUNT} 個までです`);
      return;
    }

    this.fileUtils.addFiles(this.selectedFiles, newFiles, updated => {
      this.updateSelectedFiles(updated);
      const input = event.target as HTMLInputElement;
      if (input.files) {
        input.value = ''; // 同じファイルでも選び直し可能にする
      }
    });
  }

  removeFile(index: number): void {
    this.fileUtils.removeFile(this.selectedFiles, index, updated => {
      this.updateSelectedFiles(updated);
    });
  }

  downloadAttachment(attachmentId: number): void {
    this.inquiryService.downloadAttachment(attachmentId).subscribe({
      next: (res: HttpResponse<Blob>) => {
        this.fileUtils.downloadAttachment(res);
      },
      error: () => {
        this.snackBar.show('ダウンロードに失敗しました');
      }
    });
  }

  private updateSelectedFiles(files: File[]) {
      this.selectedFiles = files;
      this.form.get('files')?.setValue(files);
      this.form.get('files')?.updateValueAndValidity();
  }

  openTransaction(id: number) {
    blankOpen(undefined, '/client/admin/withdrawalTransaction/' + id);
  }
}

